import React, { useEffect, useMemo, useState } from 'react';
import Panel from './Panel/Panel';
import { getService } from '@Shared.Angular/flowingly.hooks/useService';
import { SearchEntityType } from '@Shared.Angular/flowingly.services/flowingly.constants';
import IFlowinglySearchResult from '@Shared.Angular/@types/core/contracts/queryModel/search/flowinglySearchResult';
import Search from '@Shared.Angular/flowingly.components/Search/Search';

interface Props {
  initialSelectedItems?: IFlowinglySearchResult[];
  onChange: (items: IFlowinglySearchResult[]) => void;
  headerText: string;
  infoText?: string;
  userListAllowedPermissions?: string[];
}

const SmartSelect: React.FC<Props> = ({
  headerText,
  infoText,
  initialSelectedItems = [],
  userListAllowedPermissions = [],
  onChange
}) => {
  const smartSelectService = getService('smartSelectService');

  const [mode, setMode] = useState<SearchEntityType>(SearchEntityType.USER);
  const [availableItems, setAvailableItems] = useState<
    IFlowinglySearchResult[]
  >([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedList, setSelectedList] =
    useState<IFlowinglySearchResult[]>(initialSelectedItems);
  const [groups, setGroups] = useState<IFlowinglySearchResult[] | null>(null);

  useEffect(() => {
    if (selectedList?.length === 0 && initialSelectedItems?.length === 0) {
      return;
    }
    setSelectedList(initialSelectedItems);
  }, [initialSelectedItems]);

  useEffect(() => {
    onChange(selectedList);
  }, [selectedList, onChange]);

  useEffect(() => {
    const debounceTimeout = 500;
    const handler = setTimeout(() => {
      fetchItems(
        mode,
        searchTerm,
        userListAllowedPermissions,
        setAvailableItems
      );
    }, debounceTimeout);

    return () => clearTimeout(handler);
  }, [mode, searchTerm]);

  const fetchItems = async (
    mode: SearchEntityType,
    searchTerm: string,
    userListAllowedPermissions: string[],
    setData: (data: IFlowinglySearchResult[]) => void
  ) => {
    if (mode === SearchEntityType.USER) {
      const response = await smartSelectService.getUsers(
        searchTerm,
        userListAllowedPermissions
      );
      const items = response.data.userDetailForModals;
      smartSelectService.setHasMoreResults(response.data.hasMoreResults);
      setData(
        items.map((item) => ({
          id: item.id,
          displayName: item.fullName ?? '',
          searchEntityType: mode
        }))
      );
    } else {
      let availableGroups = groups;
      if (!availableGroups) {
        const response = await smartSelectService.getGroups();
        const items = response.data.dataModel;
        availableGroups = items.map((item) => ({
          id: item.id,
          displayName: item.displayName ?? '',
          searchEntityType: mode
        }));
        setGroups(availableGroups);
      }

      const filtered = availableGroups.filter((item) =>
        item.displayName.toLowerCase().includes(searchTerm.toLowerCase())
      );

      setData(filtered);
    }
  };

  const hasMoreUserResults = () =>
    smartSelectService.getHasMoreResults() && mode === SearchEntityType.USER;

  const searchList = useMemo(
    () =>
      availableItems.filter((d) => !selectedList.some((s) => s.id === d.id)),
    [availableItems, selectedList]
  );

  const add = (item: IFlowinglySearchResult) =>
    setSelectedList((prev) =>
      prev.some((x) => x.id === item.id) ? prev : [...prev, item]
    );

  const remove = (item: IFlowinglySearchResult) =>
    setSelectedList((prev) => prev.filter((x) => x.id !== item.id));

  const addAll = () => setSelectedList((prev) => [...prev, ...searchList]);

  const removeAll = () => setSelectedList([]);

  return (
    <div className="smart-select-container">
      <div className="left-section">
        <div className="header-block">
          <div className="mode-toggle">
            <ul className="inline-list">
              <li>
                <input
                  type="radio"
                  name="mode"
                  id="user-mode"
                  value={SearchEntityType.USER}
                  checked={mode === SearchEntityType.USER}
                  onChange={() => {
                    setMode(SearchEntityType.USER);
                    setAvailableItems([]);
                  }}
                />
                <label htmlFor="user-mode">User</label>
              </li>
              <li>
                <input
                  type="radio"
                  name="mode"
                  id="team-mode"
                  value={SearchEntityType.GROUP}
                  checked={mode === SearchEntityType.GROUP}
                  onChange={() => {
                    setMode(SearchEntityType.GROUP);
                    setAvailableItems([]);
                  }}
                />
                <label htmlFor="team-mode">Team</label>
              </li>
            </ul>
          </div>
          <div className="smart-select-search-container">
            <Search
              value=""
              placeholder="search"
              onValueChange={setSearchTerm}
            />
          </div>
        </div>

        <div className="panel-wrapper">
          <Panel
            items={searchList}
            onItemClick={add}
            iconClass="fa-light fa-circle-plus fa-lg"
            backgroundColor="#f3f3f3"
          />
        </div>

        {hasMoreUserResults() && (
          <div className="more-result-text">
            Please search to see additional users
          </div>
        )}
        {searchList.length > 0 && !hasMoreUserResults() && (
          <a className="add-all-button" onClick={addAll}>
            Add All
          </a>
        )}
      </div>

      <div className="right-section">
        <div className="header-block header-block-right">
          <h5 className="header-text">{headerText}</h5>
          <p className="info-text">{infoText}</p>
        </div>

        <div className="panel-wrapper">
          <Panel
            items={selectedList}
            onItemClick={remove}
            iconClass="fa-light fa-circle-minus fa-lg"
            backgroundColor="#ddf4de"
          />
        </div>
        {selectedList.length > 0 && !hasMoreUserResults() && (
          <a className="remove-all-button" onClick={removeAll}>
            Remove All
          </a>
        )}
      </div>
    </div>
  );
};

export default SmartSelect;
